import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiLoader, FiAlertCircle } from 'react-icons/fi';
import ProductCard from './ProductCard';
import productService from '../../services/productService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import { getImageUrl } from '../../utils/imageUrl';
import './FeaturedProductsSection.css';

const LIMIT = 12;

/**
 * Infinite-scroll product grid for cash / installment landing pages.
 * @param {{ channel: 'cash' | 'installment', title: string }} props
 */
const FeaturedProductsSection = ({ channel, title, subtitle }) => {
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingInit, setLoadingInit] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const sentinelRef = useRef(null);

    const fetchProducts = useCallback(
        async (pageNum, append = false) => {
            try {
                if (append) setLoadingMore(true);
                else setLoadingInit(true);
                setError('');

                const { products: newProducts, pagination } =
                    await productService.getAll(
                        pageNum,
                        LIMIT,
                        '',
                        'all',
                        'default',
                        channel
                    );

                setProducts((prev) =>
                    append ? [...prev, ...newProducts] : newProducts
                );
                setHasMore(pagination.hasNextPage || false);
                setTotalCount(pagination.totalCount || 0);
                setPage(pageNum);
            } catch (err) {
                setError(
                    err.message || 'Failed to load products. Please try again.'
                );
            } finally {
                setLoadingInit(false);
                setLoadingMore(false);
            }
        },
        [channel]
    );

    useEffect(() => {
        fetchProducts(1, false);
    }, [fetchProducts]);

    const handleRealtimeRefresh = useCallback(() => {
        fetchProducts(1, false);
    }, [fetchProducts]);

    useRealtimeSync('products', handleRealtimeRefresh);
    useRealtimeSync('prices', handleRealtimeRefresh);
    useRealtimeSync('categories', handleRealtimeRefresh);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasMore &&
                    !loadingMore &&
                    !loadingInit
                ) {
                    fetchProducts(page + 1, true);
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loadingInit, page, fetchProducts]);

    return (
        <section className='featured-products-section'>
            <div className='section-header text-center'>
                <span className='section-eyebrow'>
                    {channel === 'installment' ? 'EASY OWNERSHIP' : 'HOT CASH OFFERS'}
                </span>
                <h2 className='section-title'>{title}</h2>
                {subtitle && <p className='section-subtitle'>{subtitle}</p>}
            </div>

            {error && (
                <div className='featured-products-error'>
                    <FiAlertCircle />
                    <span>{error}</span>
                </div>
            )}

            {loadingInit ? (
                <div className='products-grid'>
                    {[...Array(LIMIT)].map((_, i) => (
                        <div key={i} className='product-skeleton' />
                    ))}
                </div>
            ) : products.length === 0 ? (
                <div className='featured-products-empty'>
                    <FiAlertCircle className='empty-icon' />
                    <p>No products in this section yet.</p>
                </div>
            ) : (
                <>
                    <div className='products-grid'>
                        {products.map((product) => (
                            <ProductCard
                                key={product._id}
                                product={{
                                    ...product,
                                    image: getImageUrl(product.image),
                                    _imagePath: product.image,
                                    rating: product.rating ?? 0,
                                    reviews: product.reviews ?? 0,
                                    brand: product.brand || 'Rahber',
                                }}
                            />
                        ))}
                    </div>
                    <div ref={sentinelRef} className='scroll-sentinel' />
                    {loadingMore && (
                        <div className='load-more-spinner'>
                            <FiLoader className='spin-icon' />
                            <span>Loading more products…</span>
                        </div>
                    )}
                    {!hasMore && !loadingMore && products.length > 0 && (
                        <p className='end-of-list'>
                            All {totalCount} product
                            {totalCount !== 1 ? 's' : ''} loaded
                        </p>
                    )}
                </>
            )}
        </section>
    );
};

export default FeaturedProductsSection;
